import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNoteOptions } from './fetchDeliveryNoteOptions';
import { type DeliveryNoteOption } from './types';

const MIN_QUERY_LENGTH = 2;

/**
 * Generates the query options for the delivery-note picker search, e.g. for
 * loaders. Disabled via `skipToken` until the query has at least 2 characters.
 *
 * @param query - The fulltext search query or a pasted asset id.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getDeliveryNoteOptionsQueryOptions = <
  TData = DeliveryNoteOption[],
>({
  query,
  options,
}: {
  query: string;
  options?: Omit<
    UseQueryOptions<DeliveryNoteOption[], Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const trimmedQuery = query.trim();

  return queryOptions({
    gcTime: ms('30s'),
    queryFn:
      trimmedQuery.length >= MIN_QUERY_LENGTH
        ? async ({ signal }: QueryFunctionContext) =>
            fetchDeliveryNoteOptions(trimmedQuery, { signal })
        : skipToken,
    queryKey: queryKeysDeliveryNote.getOptions(trimmedQuery),
    staleTime: ms('30s'),
    ...options,
  });
};
